import { useCurrentConfiguration } from '@/features/devices/api/version'
import { Spinner, Tooltip, TooltipContent, TooltipTrigger } from '@/ui-kit'
import { useUiKit } from '@/ui-kit/providers'
import dayjs from 'dayjs'
import { Info } from 'lucide-react'
import { ReactNode } from 'react'
import { useTopReferrals } from '../api'
import { Referral } from '../api/models'
import { ReferralsMobileList } from './ReferralsMobileList'
import { ReferralsTable } from './ReferralsTable'

type ColumnDef<T> = {
  name: string
  value: (worker: T) => string | ReactNode
  cellClassName?: string
}

export type ReferralsListProps = {
  columns: ColumnDef<Referral>[]
  referrals: Referral[]
}

const COLUMN_DEFS = ({ activationHours, activationBonus }: { activationHours: number; activationBonus: number }) => [
  {
    name: 'Referral',
    value: (referral: Referral) => referral.email,
    excludeOnMobile: true
  },
  {
    name: 'Created',
    value: (referral: Referral) => (
      <div className="relative">{dayjs(referral.createdAt).format('MM/DD/YYYY HH:mm')}</div>
    )
  },
  {
    name: 'Status',
    value: (referral: Referral) => {
      const isPending = !referral.referralActivationRewarded

      return isPending ? (
        <Tooltip>
          <TooltipTrigger>
            <div className="relative flex items-center gap-2">
              Pending
              <Info size={16} />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span className="text-xs">Status updates to Active after {activationHours} hours online</span>
          </TooltipContent>
        </Tooltip>
      ) : (
        <div className="relative flex items-center gap-2">Active</div>
      )
    }
  },
  {
    name: 'Tier',
    cellClassName: 'text-end',
    value: (referral: Referral) => <div className="relative">{referral.tierDepth}</div>
  },
  {
    name: 'Your reward',
    cellClassName: 'text-end !pr-6',
    value: (referral: Referral) => (
      <div className="relative">
        {Math.round(referral.referralPointsEarned || 0)}{' '}
        {!referral.referralActivationRewarded && referral.tierDepth === 1 ? `(+${activationBonus} pending)` : ''}
      </div>
    )
  }
]

export const ReferralsList = () => {
  const { data, isFetching } = useTopReferrals()
  const referrals = data?.items || []
  const { isMobile } = useUiKit()
  const { data: config } = useCurrentConfiguration()

  return (
    <div className="flex flex-col">
      {isFetching ? (
        <div className="flex w-full justify-center">
          <Spinner className="mt-8" />
        </div>
      ) : (
        <>
          {isMobile ? (
            <ReferralsMobileList
              columns={COLUMN_DEFS({
                activationBonus: config?.referral_activation_referrer_points || 0,
                activationHours: config?.referral_activation_hours || 0
              })}
              referrals={referrals}
            />
          ) : (
            <ReferralsTable
              columns={COLUMN_DEFS({
                activationBonus: config?.referral_activation_referrer_points || 0,
                activationHours: config?.referral_activation_hours || 0
              })}
              referrals={referrals}
            />
          )}
        </>
      )}
    </div>
  )
}
